import {
  ConstantItems,
  getDefaultConfig,
  getIncreasedTimeoutConfig,
} from "../../constants";
import * as _ from "lodash";
import { config } from "aws-sdk";
import { Locality } from "../cmodel/cmodel.repo";
import { portfolioadmin } from "../portfolioadmin/portfolioadmin";
import { AggregatePortfolioItem } from "./models/aggregatePortfolioItem";
import { TrailingTwelveMonthsPerformance } from "../portfolioadmin/reportManagement/models/TrailingTwelveMonthsPerformance";

export class PortfolioRepo {
  static $inject = ["$http", "config"];

  constructor(
    private $http: angular.IHttpService,
    private config: ConstantItems
  ) {}

  getPortfolio(portfolioId: number) {
    return this.$http.get(`${this.config.portfolioUrl}/${portfolioId}`);
  }

  getUserPortfolios() {
    return this.$http.get(`${this.config.portfolioUrl}/UserPortfolios`);
  }

  filterPortfolios(searchText: string) {
    return this.$http.get(
      `${this.config.portfolioUrl}/filter?searchText=${searchText}`
    );
  }

  savePortfolio(portfolio: any, clientNumber: number) {
    if (portfolio.portfolioId) {
      return this.$http.put(
        `${this.config.portfolioUrl}/${portfolio.portfolioId}`,
        portfolio
      );
    } else {
      return this.$http.post(
        `${this.config.portfolioUrl}?ClientNumber=${clientNumber}`,
        portfolio
      );
    }
  }

  GetUserPortfolioCount() {
    return this.$http.get(`${this.config.portfolioUrl}/user`);
  }

  private generateArguments(...args: Array<string>) {
    var parameterString = "";
    _.forEach(args, (arg) => {
      if (!_.isNull(arg) && !_.isEmpty(arg)) {
        if (_.isEmpty(parameterString)) parameterString = "?";
        else parameterString += "&";

        parameterString += arg;
      }
    });
    return parameterString;
  }

  getPortfolioAggregatesByLocality(
    clientNumber: string,
    currency: string,
    portfolioIds?: Array<number>,
    toDate?: Date,
    isReconciled: boolean = false
  ) {
    let queryParams = {
      clientNumber: clientNumber,
      portfolioIds: portfolioIds,
      currency: currency,
      date: toDate,
      isReconciled,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http
      .get(`${this.config.portfolioUrl}/localities`, config)
      .then((resp) => resp.data as AggregatePortfolioItem[]);
  }

  getPortfolioAggregatesByAssetClasses(
    clientNumber: string,
    displayCurrency: string,
    locality?: string,
    portfolioIds?: Array<number>,
    toDate?: Date,
    isReconciled: boolean = false
  ) {
    let queryParams = {
      clientNumber: clientNumber,
      currency: displayCurrency,
      locality: locality,
      portfolioIds: portfolioIds,
      date: toDate,
      isReconciled,
    };

    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http
      .get(`${this.config.portfolioUrl}/assetclasses`, config)
      .then((resp) => resp.data as AggregatePortfolioItem[]);
  }

  getPortfolioAggregatesBySector(
    currency: string,
    portfolioIds?: Array<number>,
    toDate?: any,
    locality?: string,
    isReconciled: boolean = false
  ) {
    let queryParams = {
      portfolioIds: portfolioIds,
      currency: currency,
      date: toDate,
      locality: locality,
      isReconciled,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http
      .get(`${this.config.portfolioUrl}/sectors`, config)
      .then((resp) => resp.data as AggregatePortfolioItem[]);
  }

  getPortfolioAggregatesByGeography(
    currency,
    portfolioIds?: Array<number>,
    toDate?: any,
    locality?: string,
    isReconciled: boolean = false
  ) {
    let queryParams = {
      portfolioIds: portfolioIds,
      currency: currency,
      date: toDate,
      locality: locality,
      isReconciled,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http
      .get(`${this.config.portfolioUrl}/geographies`, config)
      .then((resp) => resp.data as AggregatePortfolioItem[]);
  }

  getPortfolioAggregatesByCurrency(
    currency,
    portfolioIds?: Array<number>,
    toDate?: any,
    locality?: string,
    isReconciled: boolean = false
  ) {
    let queryParams = {
      portfolioIds: portfolioIds,
      currency: currency,
      date: toDate,
      locality: locality,
      isReconciled,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http
      .get(`${this.config.portfolioUrl}/currencies`, config)
      .then((resp) => resp.data as AggregatePortfolioItem[]);
  }

  getPortfolioCurrencies(portfolioId: number) {
    return this.$http.get(
      `${this.config.portfolioUrl}/${portfolioId}/currencies`
    );
  }

  getPortfolioPerformance(
    currency: string,
    portfolios: any,
    fromDate: any = null,
    toDate: any = null,
    isReconciled: boolean = false
  ) {
    let queryParams = {
      currency: currency,
      portfolioIds: portfolios,
      fromDate: fromDate,
      toDate: toDate,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get<any[]>(
      `${this.config.portfolioUrl}/performance${
        isReconciled ? "-reconciled" : ""
      }`,
      config
    );
  }

  getTrailingTwelveMonthsPerformance(
    currency: string,
    portfolios: any,
    toDate: any = null,
    isReconciled: boolean = false
  ) {
    let queryParams = {
      currency: currency,
      portfolioIds: portfolios,
      toDate: toDate,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get<TrailingTwelveMonthsPerformance>(
      `${this.config.portfolioUrl}/performance/trailing-twelve-months${
        isReconciled ? "-reconciled" : ""
      }`,
      config
    );
  }

  getFinancialYearToDatePerformance(
    currency: string,
    portfolios: number[],
    finYearStart: number,
    toDate: string,
    isReconciled: boolean = false
  ) {
    let queryParams = {
      currency,
      portfolioIds: portfolios,
      finYearStart,
      toDate,
    };
    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get<TrailingTwelveMonthsPerformance>(
      `${this.config.portfolioUrl}/performance/financial-year-to-date${
        isReconciled ? "-reconciled" : ""
      }`,
      config
    );
  }

  getPortfolioSummaryPerformance(
    currency: string,
    portfolios: any,
    fromDate: any = null,
    toDate: any = null,
    isReconciled: boolean = false
  ) {
    let queryParams = {
      currency: currency,
      portfolioIds: portfolios,
      fromDate: fromDate,
      toDate: toDate,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.portfolioUrl}/performance/summary${
        isReconciled ? "-reconciled" : ""
      }`,
      config
    );
  }

  getSectors() {
    let queryParams = {};
    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(`${this.config.portfolioUrl}/sectorsList`, config);
  }

  getPortfolioEquityItems(
    displayCurrency: string,
    locality?: string,
    portfolioIds?: Array<number>,
    date?: string,
    isReconciled: boolean = false
  ) {
    let queryParams = {
      displayCurrency: displayCurrency,
      locality: locality,
      portfolioIds: portfolioIds,
      date: date,
    };

    const config = getDefaultConfig();
    config.params = queryParams;
    const route = isReconciled ? "reconciled-equity-items" : "equityItems";
    return this.$http.get(`${this.config.portfolioUrl}/${route}`, config);
  }

  getPortfolioCashItems(
    displayCurrency: string,
    locality?: string,
    portfolioIds?: Array<number>,
    date?: string,
    isReconciled: boolean = false
  ) {
    let queryParams = {
      displayCurrency: displayCurrency,
      locality: locality,
      portfolioIds: portfolioIds,
      date: date,
    };

    let config = getDefaultConfig();
    config.params = queryParams;
    const route = isReconciled ? "reconciled-cash-items" : "cashItems";
    return this.$http.get<any[]>(`${this.config.portfolioUrl}/${route}`, config);
  }

  getPortfolioFixedIncomeItems(
    displayCurrency: string,
    locality?: string,
    portfolioIds?: Array<number>,
    date?: string,
    isReconciled: boolean = false
  ) {
    let queryParams = {
      displayCurrency: displayCurrency,
      locality: locality,
      portfolioIds: portfolioIds,
      date: date,
    };

    let config = getDefaultConfig();
    config.params = queryParams;
    const route = isReconciled
      ? "reconciled-fixed-income-items"
      : "fixedIncomeItems";
    return this.$http.get(`${this.config.portfolioUrl}/${route}`, config);
  }

  getPortfolioPreciousMetalItems(
    displayCurrency: string,
    locality?: string,
    portfolioIds?: Array<number>,
    date?: string,
    isReconciled: boolean = false
  ) {
    let queryParams = {
      displayCurrency: displayCurrency,
      locality: locality,
      portfolioIds: portfolioIds,
      date: date,
    };

    let config = getDefaultConfig();
    config.params = queryParams;
    const route = isReconciled
      ? "reconciled-precious-metal-items"
      : "preciousMetalItems";
    return this.$http.get(`${this.config.portfolioUrl}/${route}`, config);
  }

  // Methods to get data for the portfolio admin page:
  getPortfolioPreciousMetalItemsForAdmin(
    clientId: number,
    portfolioIds?: Array<number>,
    displayCurrency?: string,
    locality?: string
  ) {
    let queryParams = {
      clientId: clientId,
      portfolioIds: portfolioIds,
      displayCurrency: displayCurrency,
      locality: locality,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.portfolioManagementUrl}/preciousMetalitems`,
      config
    );
  }

  getPortfolioAlternativeInvestmentsItemsForAdmin(
    clientId: number,
    portfolioIds?: Array<number>,
    displayCurrency?: string,
    locality?: string
  ) {
    let queryParams = {
      clientId: clientId,
      portfolioIds: portfolioIds,
      displayCurrency: displayCurrency,
      locality: locality,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.portfolioManagementUrl}/alternativeInvestmentsItems`,
      config
    );
  }

  getPortfolioAlternativeInvestmentItems(
    displayCurrency: string,
    locality?: string,
    portfolioIds?: Array<number>,
    date?: string,
    isReconciled: boolean = false
  ) {
    let queryParams = {
      displayCurrency: displayCurrency,
      locality: locality,
      portfolioIds: portfolioIds,
      date: date,
    };

    let config = getDefaultConfig();
    config.params = queryParams;
    const route = isReconciled
      ? "reconciled-precious-metal-items"
      : "alternativeInvestmentsItems";
    return this.$http.get(`${this.config.portfolioUrl}/${route}`, config);
  }

  getPortfolioCashItemsForAdmin(
    clientId: number,
    portfolioIds?: Array<number>,
    displayCurrency?: string,
    locality?: string
  ) {
    let queryParams = {
      clientId: clientId,
      portfolioIds: portfolioIds,
      displayCurrency: displayCurrency,
      locality: locality,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.portfolioManagementUrl}/cashItems`,
      config
    );
  }

  getPortfolioEquityItemsForAdmin(
    clientId: number,
    portfolioIds?: Array<number>,
    displayCurrency?: string,
    locality?: string
  ) {
    let queryParams = {
      clientId: clientId,
      portfolioIds: portfolioIds,
      displayCurrency: displayCurrency,
      locality: locality,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.portfolioManagementUrl}/equityItems`,
      config
    );
  }

  getPortfolioFixedIncomeItemsForAdmin(
    clientId: number,
    portfolioIds?: Array<number>,
    displayCurrency?: string,
    locality?: string
  ) {
    let queryParams = {
      clientId: clientId,
      portfolioIds: portfolioIds,
      displayCurrency: displayCurrency,
      locality: locality,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.portfolioManagementUrl}/fixedIncomeItems`,
      config
    );
  }

  getPortfolioCashAccounts(
    portfolioIds: Array<number>,
    isReconciled: boolean = false
  ) {
    let queryParams = {
      portfolioIds: portfolioIds,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.portfolioUrl}/cash/accounts?isReconciled=${isReconciled}`,
      config
    );
  }

  getCashAccountTransactions(accountId: number, isReconciled: boolean = false) {
    return this.$http.get(
      `${this.config.portfolioUrl}/cash/accounts/${accountId}/transactions?isReconciled=${isReconciled}`
    );
  }

  getTradeCurrencies(portfolioIds: Array<number>) {
    let queryParams = {
      portfolioIds: portfolioIds,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.portfolioUrl}/trades/currencies`,
      config
    );
  }

  getTrades(portfolioIds: Array<number>, currency?: string) {
    let queryParams = {
      portfolioIds: portfolioIds,
      currency: currency,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(`${this.config.portfolioUrl}/trades`, config);
  }

  getPerformanceParameters(
    portfolioId: number,
    currency: string,
    fromDate: any,
    toDate: any,
    isReconciled: boolean = false
  ) {
    let queryParams = {
      portfolioId: portfolioId,
      currency: currency,
      fromDate: fromDate,
      toDate: toDate,
    };
    let config = getIncreasedTimeoutConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.portfolioUrl}/performances/management/parameters${
        isReconciled ? "-reconciled" : ""
      }`,
      config
    );
  }

  getCashTransactionsForPortfolio(
    portfolioId: number,
    fromDate: any,
    toDate: any,
    locality: any,
    isReconciled: boolean = false
  ) {
    let queryParams = {
      portfolioId: portfolioId,
      fromDate: fromDate,
      toDate: toDate,
      locality: locality,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.portfolioUrl}/cash/accounts/transactions?isReconciled=${isReconciled}`,
      config
    );
  }

  getLastTransactionsForPortfolios(portfolioIds: Array<number>, date: any) {
    let queryParams = {
      portfolioIds: portfolioIds,
      date: date,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.portfolioUrl}/cash/accounts/last-transactions`,
      config
    );
  }

  saveCashTransaction(cashTransaction: any, isReconciled: boolean = false) {
    return this.$http.put(
      `${this.config.portfolioUrl}/cash/accounts/transactions?isReconciled=${isReconciled}`,
      cashTransaction
    );
  }

  recalculatePerformance(
    portfolioIds: Array<number>,
    fromDate: any,
    toDate: any,
    forceCalculate: boolean = false,
    isReconciled: boolean = false
  ) {
    let queryParams = {
      portfolioIds: portfolioIds,
      fromDate: fromDate,
      toDate: toDate,
      forceCalculate: forceCalculate,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.put(
      `${this.config.portfolioUrl}/performance/recalculate${
        isReconciled ? "-reconciled" : ""
      }`,
      null,
      config
    );
  }

  recalculateYearlyPerformance(
    portfolioIds: Array<number>,
    fromDate: any,
    toDate: any,
    forceCalculate: boolean = false,
    isReconciled: boolean = false
  ) {
    let queryParams = {
      portfolioIds: portfolioIds,
      fromDate: fromDate,
      toDate: toDate,
      forceCalculate: forceCalculate,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.put(
      `${this.config.portfolioUrl}/performance/recalculateYearly${
        isReconciled ? "-reconciled" : ""
      }`,
      null,
      config
    );
  }

  getCashTransactionTypes(accountId: number) {
    let queryParams = {
      cashAccountId: accountId,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.portfolioUrl}/cash/transactionTypes`,
      config
    );
  }

  addCashTransaction(
    accountId: number,
    cashTransaction: any,
    isReconciled: boolean = false
  ) {
    // cash/accounts/{AccountId}/transactions
    return this.$http.post(
      `${this.config.portfolioUrl}/cash/accounts/${accountId}/transactions?isReconciled=${isReconciled}`,
      cashTransaction
    );
  }

  getPortfolioManagerClientSummary(currency: string) {
    let queryParams = {
      selectedCurrency: currency,
    };

    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.get(
      `${this.config.portfolioManagementDashboardUrl}/clients/summary`,
      config
    );
  }

  getMonthlyPerformanceParameters = (
    portfolioId: number,
    locality: any,
    performanceDate: Date,
    isReconciled: boolean = false
  ) => {
    let queryParams = {
      portfolioId: portfolioId,
      locality: locality,
      performanceDate: performanceDate,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.portfolioUrl}/performances/management/parameters/monthly${
        isReconciled ? "-reconciled" : ""
      }`,
      config
    );
  };

  updatePerformanceParameters = (
    portfolioId: number,
    locality: any,
    performanceParameters: any,
    isReconciled: boolean = false
  ) => {
    let queryParams = {
      portfolioId: portfolioId,
      locality: locality,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.put(
      `${this.config.portfolioUrl}/performance/monthly${
        isReconciled ? "-reconciled" : ""
      }`,
      performanceParameters,
      config
    );
  };

  getClientPortfolioRiskProfile = (clientId: number) => {
    return this.$http.get(
      `${this.config.portfolioUrl}/riskprofiles/${clientId}`
    );
  };

  savePortfolioRiskProfile = (
    portfolioId: number,
    portfolioRiskProfile: any
  ) => {
    return this.$http.post(
      `${this.config.portfolioUrl}/riskprofiles/${portfolioId}`,
      portfolioRiskProfile
    );
  };

  generatePortfolioManagerClientSecurityBalancesExcel = (toDate = null) => {
    let queryParams = {
      toDate: toDate,
    };

    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.get(
      `${this.config.portfolioManagementDashboardUrl}/clients/summary/generate`,
      config
    );
  };

  getPortfolioSecurities = (portfolioId: number, assetClass: string) => {
    let queryParams = {
      portfolioId: portfolioId,
      assetClass: assetClass,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(`${this.config.portfolioUrl}/securities`, config);
  };

  togglePerformanceLock = (
    portfolioId,
    performanceDate,
    lockPerformance,
    performanceType,
    isReconciled: boolean = false
  ) => {
    let queryParams = {
      portfolioId: portfolioId,
      performanceDate: performanceDate,
      lockPerformance: lockPerformance,
      performanceType: performanceType,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.put(
      `${this.config.portfolioUrl}/performance/lock${
        isReconciled ? "-reconciled" : ""
      }`,
      null,
      config
    );
  };

  toggleCombinedPerformanceLock = (
    id,
    lockPerformance,
    isReconciled: boolean = false
  ) => {
    let queryParams = {
      id: id,
      lockPerformance: lockPerformance,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.put(
      `${this.config.portfolioUrl}/performance/combined/lock${
        isReconciled ? "-reconciled" : ""
      }`,
      null,
      config
    );
  };

  deleteCashTransaction = (
    cashTransactionId,
    isReconciled: boolean = false
  ) => {
    return this.$http.delete(
      `${this.config.portfolioUrl}/cash/accounts/transactions/${cashTransactionId}/${isReconciled}`
    );
  };

  getAssetClassBreakDownByExternalSourceLocality = (
    displayCurrency = "MUR",
    externalSourceName = "",
    locality = "",
    date = null,
    displayAllClients = false
  ) => {
    let queryParams = {
      displayCurrency: displayCurrency,
      externalSourceName: externalSourceName,
      locality: locality,
      date: date,
      displayAllClients: displayAllClients,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.portfolioManagementDashboardUrl}/assetclasses/balances`,
      config
    );
  };

  getSecurityBreakDownByExternalSourceLocality = (
    displayCurrency = "MUR",
    externalSourceName = "",
    locality = "",
    assetClassName = "",
    date = null,
    displayAllClients = false
  ) => {
    let queryParams = {
      displayCurrency: displayCurrency,
      externalSourceName: externalSourceName,
      locality: locality,
      assetClassName: assetClassName,
      date: date,
      displayAllClients: displayAllClients,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.portfolioManagementDashboardUrl}/securities/balances`,
      config
    );
  };

  getCashBreakDownByExternalSourceLocality = (
    displayCurrency = "MUR",
    externalSourceName = "",
    locality = "",
    assetClassName = "",
    date = null,
    displayAllClients = false
  ) => {
    let queryParams = {
      displayCurrency: displayCurrency,
      externalSourceName: externalSourceName,
      locality: locality,
      assetClassName: assetClassName,
      date: date,
      displayAllClients: displayAllClients,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.portfolioManagementDashboardUrl}/cash/balances`,
      config
    );
  };

  getClientBalancesForSecurity = (
    securityId,
    displayCurrency = "MUR",
    externalSourceName = "",
    date = null,
    displayAllClients = false
  ) => {
    let queryParams = {
      securityId: securityId,
      displayCurrency: displayCurrency,
      displayAllClients: displayAllClients,
      externalSourceName: externalSourceName,
      date: date,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.portfolioManagementDashboardUrl}/clients/securities`,
      config
    );
  };

  getSecuritiesAndCash = () => {
    return this.$http.get(
      `${this.config.portfolioManagementDashboardUrl}/securities`
    );
  };

  getClientBalancesForCash = (
    cashAccountCurrency,
    displayCurrency = "MUR",
    externalSourceName = "",
    date = null,
    displayAllClients = false
  ) => {
    let queryParams = {
      cashAccountCurrency: cashAccountCurrency,
      displayCurrency: displayCurrency,
      displayAllClients: displayAllClients,
      externalSourceName: externalSourceName,
      date: date,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.portfolioManagementDashboardUrl}/clients/cash`,
      config
    );
  };

  updatePortfolioInception = (portfolio, isReconciled: boolean = false) => {
    let queryParams = {
      portfolioId: portfolio.portfolioId,
      inception: portfolio.inception,
      inceptionDate: portfolio.inceptionDate,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.put(
      `${this.config.portfolioUrl}/performance/inception${
        isReconciled ? "-reconciled" : ""
      }`,
      null,
      config
    );
  };

  insertYearlyPerformance = (performance, isReconciled: boolean = false) => {
    return this.$http.post(
      `${this.config.portfolioUrl}/performance/yearly${
        isReconciled ? "-reconciled" : ""
      }`,
      performance
    );
  };

  insertCombinedPerformance = (performance, isReconciled: boolean = false) => {
    return this.$http.post(
      `${this.config.portfolioUrl}/performance/combined/yearly${
        isReconciled ? "-reconciled" : ""
      }`,
      performance
    );
  };

  insertCombinedMonthlyPerformance = (
    performance,
    isReconciled: boolean = false
  ) => {
    return this.$http.post(
      `${this.config.portfolioUrl}/performance/combined/monthly${
        isReconciled ? "-reconciled" : ""
      }`,
      performance
    );
  };

  getExhangeRatesForDay = (performanceDate: string) => {
    let queryParams = {
      date: performanceDate
    };

    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.get(
      `${this.config.exchangeUrl}/get-rates-on-day`,
      config
    );
  };


  getInceptionPerformance(
    currency: string,
    clientNumber: string,
    portfolioIds?: Array<number>,
    date?: string,
    isReconciled: boolean = false
  ) {
    let queryParams = {
      clientNumber: clientNumber,
      portfolioIds: portfolioIds,
      date: date,
      currency: currency,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.portfolioUrl}/performance/inception${
        isReconciled ? "-reconciled" : ""
      }`,
      config
    );
  }

  recalculateCombinedYearlyPerformance(
    clientId: any,
    fromDate: any,
    toDate: any,
    portfolioIds?: Array<number>,
    isReconciled: boolean = false
  ) {
    let queryParams = {
      clientId: clientId,
      fromDate: fromDate,
      toDate: toDate,
      portfolioIds: portfolioIds,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.put(
      `${this.config.portfolioUrl}/performance/recalculateCombinedYearly${
        isReconciled ? "-reconciled" : ""
      }`,
      {},
      config
    );
  }

  recalculateCombinedMonthlyPerformance(
    clientId: any,
    fromDate: any,
    toDate: any,
    portfolioIds?: Array<number>,
    isReconciled: boolean = false
  ) {
    let queryParams = {
      clientId: clientId,
      fromDate: fromDate,
      toDate: toDate,
      portfolioIds: portfolioIds,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.put(
      `${this.config.portfolioUrl}/performance/recalculateCombinedMonthly${
        isReconciled ? "-reconciled" : ""
      }`,
      {},
      config
    );
  }

  getCombinedYearly(
    clientId: any,
    currency,
    portfolioIds: Array<number> = [],
    isReconciled: boolean = false
  ) {
    let queryParams = {
      clientId: clientId,
      currency: currency,
      portfolioIds: portfolioIds,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.portfolioUrl}/performance/combined/yearly${
        isReconciled ? "-reconciled" : ""
      }`,
      config
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/portfolio/portfolio.repo.ts